// Mirrors backend src/core/classification_markings.format_classification_marking.
// IC marking format per CLAUDE.md:
//   - Double slash (//) separates categories (level / compartments / dissem / releasability)
//   - Single slash (/) separates items within a category
// Example: TOP SECRET//SI/TK//NOFORN/ORCON//REL TO USA/GBR

const COMPARTMENT_TOKENS = new Set(['SI', 'TK', 'HCS', 'GAMMA', 'KLONDIKE', 'COMINT', 'RD', 'FRD', 'CNWDI', 'SIGMA', 'DELTA', 'FISA']);
const DISSEM_TOKENS = new Set(['NOFORN', 'ORCON', 'IMCON', 'PROPIN', 'FOUO', 'LIMDIS', 'NOCONTRACT', 'NF', 'OC']);
// Placeholder strings the LLM occasionally emits inside caveat lists. Drop them.
const JUNK_TOKENS = new Set(['NONE', 'NULL', 'N/A', 'NA', 'UNDEFINED', '-']);

function uniq(items) {
  const seen = new Set();
  const out = [];
  for (const it of items || []) {
    const k = String(it).trim();
    if (!k) continue;
    if (JUNK_TOKENS.has(k.toUpperCase())) continue;
    if (seen.has(k)) continue;
    seen.add(k);
    out.push(k);
  }
  return out;
}

// "X EYES ONLY" markings (e.g. TAIWAN EYES ONLY, FVEY EYES ONLY) are
// releasability restrictions — they limit who can read the doc — not
// SCI compartments. Recognize the pattern.
function isEyesOnly(token) {
  return /\bEYES\s+ONLY\b/i.test(token);
}

// Canonical builder. Pass each category as its own array. Releasability strings
// (e.g. "REL TO USA, GBR") are kept whole and joined with single-slash if multiple.
export function formatClassificationMarking({
  level,
  compartments = [],
  dissemination = [],
  releasability = [],
} = {}) {
  const lvl = String(level || 'UNCLASSIFIED').toUpperCase();
  const cats = [lvl];
  const c = uniq(compartments);
  const d = uniq(dissemination);
  const r = uniq(releasability);
  if (c.length) cats.push(c.join('/'));
  if (d.length) cats.push(d.join('/'));
  if (r.length) cats.push(r.join('/'));
  return cats.join('//');
}

// Backend response shape varies — sometimes per-paragraph code drops compartments
// into `caveats` instead of `compartments`. This sorts a mixed bag into the
// correct slot using the well-known token sets.
export function partitionCaveatsIntoSlots(rawCaveats = [], rawCompartments = [], rawReleasability = []) {
  const all = uniq([...(rawCaveats || []), ...(rawCompartments || [])]);
  const compartments = [];
  const dissemination = [];
  const releasability = uniq(rawReleasability);
  for (const item of all) {
    const upper = item.toUpperCase();
    if (DISSEM_TOKENS.has(upper)) {
      if (!dissemination.includes(upper)) dissemination.push(upper);
    } else if (COMPARTMENT_TOKENS.has(upper)) {
      if (!compartments.includes(upper)) compartments.push(upper);
    } else if (upper.startsWith('REL TO') || upper.startsWith('REL ') || isEyesOnly(upper)) {
      // "REL TO X" and "X EYES ONLY" both gate distribution — releasability slot.
      if (!releasability.includes(item)) releasability.push(item);
    } else {
      // Unknown token — treat as compartment (conservative; SCI tokens are the
      // long tail, dissem is a closed set).
      if (!compartments.includes(upper)) compartments.push(upper);
    }
  }
  // NOFORN excludes all foreign release. If both NOFORN and any
  // releasability marking are present, NOFORN wins (more restrictive).
  if (dissemination.includes('NOFORN') && releasability.length > 0) {
    releasability.length = 0;
  }
  return { compartments, dissemination, releasability };
}

// Build the full classification string from a result object. Prefers the
// component arrays (caveats/compartments/releasability) over a possibly-malformed
// `full_classification` string from the backend.
export function buildFullClassification(result) {
  if (!result) return 'UNCLASSIFIED';
  const baseLevel = String(result.classification || result.document_classification || 'UNCLASSIFIED').toUpperCase();
  const haveArrays = (result.caveats && result.caveats.length)
    || (result.compartments && result.compartments.length)
    || (result.releasability && result.releasability.length);
  if (haveArrays) {
    const slots = partitionCaveatsIntoSlots(result.caveats, result.compartments, result.releasability);
    return formatClassificationMarking({ level: baseLevel, ...slots });
  }
  // Fall back to the backend string if no arrays. Last resort.
  return result.full_classification || baseLevel;
}
